// ═══════════════════════════════════════════════════════════════════════════════
// FDH Shared Constants
// ═══════════════════════════════════════════════════════════════════════════════
import programPackageJson from '../../../../package.json' with { type: 'json' };
// ── Application Info ──
export const APP_NAME = 'MaidEasy';
export const APP_VERSION = programPackageJson.version;
export const PLAY_STORE_PACKAGE_ID = 'com.hfsworld.maid.easy';
export const PLAY_STORE_LISTING_URL = `https://play.google.com/store/apps/details?id=${PLAY_STORE_PACKAGE_ID}`;
// ── Approval Status (task / receipt) ──
export const APPROVAL_STATUS = {
    PENDING: 'pending',
    APPROVED: 'approved',
    REJECTED: 'rejected',
    NEEDS_UPDATE: 'needs_update',
    CANCEL: 'cancel',
};
// ── Receipt Status ──
export const RECEIPT_STATUS = {
    SAVE: 'save',
    SUBMITTED: 'submitted',
    REVIEWED: 'reviewed',
};
// ── Settlement Status ──
export const SETTLEMENT_STATUS = {
    SUBMITTED: 'submitted',
    REJECTED: 'rejected',
    WITHDRAWN: 'withdrawn',
    REVIEWED: 'reviewed',
    UNLOCKED: 'unlocked',
};
// ── Recurrence Types ──
export const RECURRENCE_TYPE = {
    NONE: 'none',
    DAILY: 'daily',
    WEEKLY: 'weekly',
    BIWEEKLY: 'biweekly',
    MONTHLY: 'monthly',
};
/** Subtype when `recurrence === monthly`. */
export const MONTHLY_KIND = {
    DAY: 'day',
    NTH_WEEKDAY: 'nth_weekday',
    LAST_WEEKDAY: 'last_weekday',
};
// ── User Roles ──
export const USER_ROLE = {
    EMPLOYER: 'employer',
    HELPER: 'helper',
};
// ── Authentication Providers ──
export const AUTH_PROVIDER = {
    LOCAL: 'local',
    GOOGLE: 'google',
};
// ── Admin Roles ──
export const ADMIN_ROLE = {
    SUPER_ADMIN: 'super_admin',
    SUPPORT_ADMIN: 'support_admin',
};
// ── Pairing Status ──
export const PAIRING_STATUS = {
    ACTIVE: 'active',
    INACTIVE: 'inactive',
    TERMINATED: 'terminated',
};
// ── Notification Types ──
export const NOTIFICATION_TYPE = {
    TASK_SUBMITTED: 'task_submitted',
    TASK_REVIEWED: 'task_reviewed',
    RECEIPT_SUBMITTED: 'receipt_submitted',
    RECEIPT_REVIEWED: 'receipt_reviewed',
    SETTLEMENT_LOCKED: 'settlement_locked',
    SETTLEMENT_UNLOCKED: 'settlement_unlocked',
    SETTLEMENT_REVIEWED: 'settlement_reviewed',
    SETTLEMENT_REJECTED: 'settlement_rejected',
    SETTLEMENT_WITHDRAWN: 'settlement_withdrawn',
    SETTLEMENT_SUBMITTED: 'settlement_submitted',
    BUDGET_CREATED: 'budget_created',
    BUDGET_UPDATED: 'budget_updated',
    BUDGET_DELETED: 'budget_deleted',
    COMMENT_ADDED: 'comment_added',
    PAIRING_COMPLETED: 'pairing_completed',
    CONSENT_REQUIRED: 'consent_required',
    INVENTORY_LOW: 'inventory_low',
    INVENTORY_EXPIRY: 'inventory_expiry',
    PIN_MESSAGE_PUBLISHED: 'pin_message_published',
    PIN_MESSAGE_UPDATED: 'pin_message_updated',
    TASK_CREATED_FOR_HELPER: 'task_created_for_helper',
    TASK_UPDATED_FOR_HELPER: 'task_updated_for_helper',
    TASK_DUE_REMINDER_FOR_HELPER: 'task_due_reminder_for_helper',
    MEMBERSHIP_DOWNGRADED: 'membership_downgraded',
    TRIAL_EXPIRING: 'trial_expiring',
    TRIAL_ENDED: 'trial_ended',
    SUBSCRIPTION_PAYMENT_FAILED: 'subscription_payment_failed',
    SUBSCRIPTION_EXPIRING: 'subscription_expiring',
    SYSTEM_MESSAGE: 'system_message',
};
/** Fixed lead time when employer enables “remind helper before due”. */
export const TASK_HELPER_REMINDER_LEAD_MINUTES = 10;
// ── Comment Target Types ──
export const COMMENT_TARGET_TYPE = {
    TASK_INSTANCE: 'task_instance',
    RECEIPT: 'receipt',
};
// ── Audit Log Action Types ──
export const AUDIT_ACTION = {
    USER_REGISTER: 'user_register',
    USER_LOGIN: 'user_login',
    USER_LOGIN_FAILED: 'user_login_failed',
    USER_LOGOUT: 'user_logout',
    PASSWORD_RESET: 'password_reset',
    PASSWORD_CHANGED: 'password_changed',
    PAIRING_CREATED: 'pairing_created',
    PAIRING_REACTIVATED: 'pairing_reactivated',
    CONSENT_ACCEPTED: 'consent_accepted',
    TASK_CREATED: 'task_created',
    TASK_UPDATED: 'task_updated',
    TASK_ARCHIVED: 'task_archived',
    TASK_SUBMITTED: 'task_submitted',
    TASK_REVIEWED: 'task_reviewed',
    BUDGET_CREATED: 'budget_created',
    BUDGET_UPDATED: 'budget_updated',
    BUDGET_DELETED: 'budget_deleted',
    RECEIPT_SUBMITTED: 'receipt_submitted',
    RECEIPT_REVIEWED: 'receipt_reviewed',
    RECEIPT_DELETED: 'receipt_deleted',
    SETTLEMENT_LOCKED: 'settlement_locked',
    SETTLEMENT_UNLOCKED: 'settlement_unlocked',
    SETTLEMENT_REVIEWED: 'settlement_reviewed',
    SETTLEMENT_REJECTED: 'settlement_rejected',
    SETTLEMENT_WITHDRAWN: 'settlement_withdrawn',
    FAMILY_MEMBER_CREATED: 'family_member_created',
    FAMILY_MEMBER_UPDATED: 'family_member_updated',
    FAMILY_MEMBER_DELETED: 'family_member_deleted',
    SETTINGS_UPDATED: 'settings_updated',
    ACCOUNT_REMOVAL_REQUESTED: 'account_removal_requested',
    ACCOUNT_REMOVAL_CONFIRMED: 'account_removal_confirmed',
    ADMIN_LOGIN: 'admin_login',
    ADMIN_LOGIN_FAILED: 'admin_login_failed',
    ADMIN_LOGOUT: 'admin_logout',
    ADMIN_SETTINGS_UPDATED: 'admin_settings_updated',
    ADMIN_USER_PASSWORD_RESET: 'admin_user_password_reset',
    ADMIN_PASSWORD_RESET: 'admin_password_reset',
    ADMIN_USER_CREATED: 'admin_user_created',
    ADMIN_USER_UPDATED: 'admin_user_updated',
    ADMIN_USER_REMOVED: 'admin_user_removed',
    ADMIN_INVITATION_CODE_CREATED: 'admin_invitation_code_created',
    ADMIN_INVITATION_CODE_UPDATED: 'admin_invitation_code_updated',
    ADMIN_SYSTEM_MESSAGE_CREATED: 'admin_system_message_created',
    ADMIN_SYSTEM_MESSAGE_UPDATED: 'admin_system_message_updated',
    USER_LEVEL_CHANGED: 'user_level_changed',
    SUBSCRIPTION_AUTO_CANCELED_PAST_DUE: 'subscription_auto_canceled_past_due',
};
// ── Audit Target Types ──
export const AUDIT_TARGET_TYPE = {
    USER: 'user',
    PAIRING: 'pairing',
    TASK: 'task',
    TASK_INSTANCE: 'task_instance',
    BUDGET: 'budget',
    RECEIPT: 'receipt',
    SETTLEMENT: 'settlement',
    FAMILY_MEMBER: 'family_member',
    SYSTEM_SETTING: 'system_setting',
    ADMIN_USER: 'admin_user',
    INVITATION_CODE: 'invitation_code',
    SYSTEM_MESSAGE: 'system_message',
};
// ── Content translation entity types (user-entered text) ──
export const CONTENT_TRANSLATION_ENTITY_TYPE = {
    FAMILY_MEMBER: 'family_member',
    PIN_MESSAGE: 'pin_message',
    COMMENT: 'comment',
    RECEIPT: 'receipt',
    RECEIPT_LINE_ITEM: 'receipt_line_item',
    TASK: 'task',
    TASK_INSTANCE: 'task_instance',
    SETTLEMENT: 'settlement',
};
// ── Task Status ──
export const TASK_STATUS = {
    ACTIVE: 'active',
    ARCHIVED: 'archived',
};
// ── Theme Options ──
export const THEME = {
    LIGHT: 'light',
    DARK: 'dark',
    SYSTEM: 'system',
};
// ── Font Size Options ──
export const FONT_SIZE = {
    XSMALL: 'xsmall',
    SMALL: 'small',
    MEDIUM: 'medium',
    LARGE: 'large',
    XLARGE: 'xlarge',
};
/** Ordered from smallest to largest (settings slider + CSS data-font-size) */
export const FONT_SIZE_ORDER = [
    FONT_SIZE.XSMALL,
    FONT_SIZE.SMALL,
    FONT_SIZE.MEDIUM,
    FONT_SIZE.LARGE,
    FONT_SIZE.XLARGE,
];
export const isFontSize = (value) => Object.values(FONT_SIZE).includes(value);
// ── Supported Languages ──
export const LANGUAGE = {
    ZH_HANT: 'zh-Hant',
    ZH_HANS: 'zh-Hans',
    EN: 'en',
    ID: 'id',
    FIL: 'fil',
};
/** All UI / settings languages (excludes legacy codes like zh-HK). */
export const SUPPORTED_LANGUAGES = [
    LANGUAGE.ZH_HANT,
    LANGUAGE.ZH_HANS,
    LANGUAGE.EN,
    LANGUAGE.ID,
    LANGUAGE.FIL,
];
export const isSupportedLanguage = (value) => SUPPORTED_LANGUAGES.includes(value);
/** Endonym labels for language picker (always shown in the language itself). */
export const LANGUAGE_NATIVE_LABELS = {
    [LANGUAGE.ZH_HANT]: '繁體中文',
    [LANGUAGE.ZH_HANS]: '简体中文',
    [LANGUAGE.EN]: 'English',
    [LANGUAGE.ID]: 'Bahasa Indonesia',
    [LANGUAGE.FIL]: 'Filipino',
};
export const getLanguageNativeLabel = (language) => LANGUAGE_NATIVE_LABELS[language] ?? LANGUAGE_NATIVE_LABELS[LANGUAGE.EN];
// ── Level 1 Default Limits (runtime reads from system_settings) ──
export const LEVEL1_DEFAULTS = {
    MAX_DAILY_TASKS: 20,
    MAX_DAILY_RECEIPTS: 15,
    MAX_DAILY_AI_ANALYZE: 20,
    MAX_COMMENTS_PER_TARGET: 10,
    MAX_COMMENT_LENGTH: 200,
    PHOTO_RETENTION_DAYS: 60,
    MAX_TASK_PHOTOS: 3,
    MIN_TASK_PHOTOS: 1,
    MAX_RECEIPT_FILES_PER_REQUEST: 10,
    MAX_FILE_SIZE_BYTES: 10 * 1024 * 1024, // 10 MB
    AUDIT_LOG_RETENTION_YEARS: 3,
    /** Max line items on a single inventory list template. */
    MAX_INVENTORY_LIST_ITEMS: 100,
    /** Per pairing per calendar day: first successful inventory task submit. */
    MAX_DAILY_INVENTORY_SUBMITS: 99,
};
// ── Task kind (standard vs inventory checklist) ──
export const TASK_KIND = {
    STANDARD: 'standard',
    INVENTORY: 'inventory',
    PICKUP_DROPOFF: 'pickup_dropoff',
    MEDICATION_REMINDER: 'medication_reminder',
};
export const TASK_GROUP_CONFIG_TYPE = {
    PICKUP_DROPOFF: 'pickup_dropoff',
    MEDICATION_REMINDER: 'medication_reminder',
};
/** Max medication time slots per wizard (one task per slot). */
export const MAX_MEDICATION_REMINDER_SLOTS = 10;
// ── Inventory level (AI + human confirmation) ──
export const INVENTORY_LEVEL = {
    FULL: 'full',
    HALF: 'half',
    LOW: 'low',
    EMPTY: 'empty',
};
/** Default "expiring soon" window when employer set no notifyExpiryLeadDays (includes past-due). */
export const INVENTORY_EXPIRING_SOON_WINDOW_CALENDAR_DAYS = 7;
// ── Inventory list schedule (chain next instance after review) ──
export const INVENTORY_SCHEDULE = {
    NONE: 'none',
    WEEKLY: 'weekly',
    MONTHLY_FIRST_WDAY: 'monthly_first_wday',
};
// ── Allowed Image MIME Types ──
export const ALLOWED_IMAGE_TYPES = [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/webp',
    'image/heic',
    'image/heif',
];
// ── Date / Datetime Formats ──
export const DATE_FORMAT = 'YYYY-MM-DD';
export const DATETIME_FORMAT = 'YYYY-MM-DD HH:mm';
// ── Date/Time Regex Patterns ──
export const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;
export const DATETIME_REGEX = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/;
// ── Pairing Code Configuration ──
export const PAIRING_CODE_LENGTH = 6;
export const PAIRING_CODE_EXPIRY_MINUTES = 4320; // 3 days
export const PAIRING_CODE_CHARACTERS = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Exclude 0, O, I, 1 for readability
/** Machine-readable reasons from public pairing code preview API. */
export const PAIRING_CODE_PREVIEW_REASON = {
    FORMAT_INVALID: 'format_invalid',
    NOT_FOUND: 'not_found',
    EXPIRED: 'expired',
    ALREADY_USED: 'already_used',
    EMPLOYER_PAIRED: 'employer_paired',
};
// ── Invitation Code Configuration (employer registration) ──
export const INVITATION_CODE_LENGTH = 8;
export const INVITATION_CODE_CHARACTERS = PAIRING_CODE_CHARACTERS;
export const INVITATION_CODE_REGEX = /^[A-HJ-NP-Z2-9]{8}$/;
export const INVITATION_CODE_STATUS = {
    ACTIVE: 'active',
    DISABLED: 'disabled',
};
/** Machine-readable reasons returned by validate API (map to i18n on frontend). */
export const INVITATION_CODE_VALIDATE_REASON = {
    FORMAT_INVALID: 'format_invalid',
    NOT_FOUND: 'not_found',
    NOT_YET_ACTIVE: 'not_yet_active',
    EXPIRED: 'expired',
    DISABLED: 'disabled',
    MAX_USES_REACHED: 'max_uses_reached',
};
export const INVITATION_CODE_TYPES = {
    REGISTRATION_GRANT: 'registration_grant',
    L1_TRIAL_EXTEND: 'l1_trial_extend',
    L3_TRIAL_PERIOD: 'l3_trial_period',
};
export const INVITATION_USAGE_CONTEXT = {
    REGISTRATION: 'registration',
    REDEEM: 'redeem',
};
/** Reasons returned by employer redeem validate/redeem APIs. */
export const INVITATION_CODE_REDEEM_REASON = {
    /** Code type is registration_grant — only usable during registration. */
    INVITATION_CODE_REGISTRATION_ONLY: 'invitation_code_registration_only',
    /** Reserved: code type not allowed at registration (currently unused; all types are registration-allowed). */
    INVITATION_CODE_AFTER_REGISTRATION_ONLY: 'invitation_code_after_registration_only',
    NOT_L1_TRIAL: 'not_l1_trial',
    L1_TRIAL_ENDED: 'l1_trial_ended',
    SUBSCRIPTION_ACTIVE: 'subscription_active',
    EMAIL_ALREADY_USED: 'email_already_used',
    LEVEL_DURATION_REQUIRED: 'level_duration_required',
};
// ── Password Policy ──
export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 16;
export const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,16}$/;
// ── Nickname Policy ──
export const NICKNAME_MIN_LENGTH = 2;
export const NICKNAME_MAX_LENGTH = 50;
// ── JWT / Token Configuration ──
export const ACCESS_TOKEN_EXPIRY_MINUTES = 30;
export const REFRESH_TOKEN_EXPIRY_DAYS = 7;
export const RESET_PASSWORD_TOKEN_EXPIRY_MINUTES = 15;
// ── AI Configuration ──
/** Backend Gemini call timeout per image (phase1 §12). */
export const AI_PER_FILE_TIMEOUT_SECONDS = 60;
/** Frontend fetch timeout for POST /receipts/analyze (phase1 §12). */
export const AI_ANALYZE_FETCH_TIMEOUT_MS = 60_000;
export const AI_REQUEST_TIMEOUT_SECONDS = 180;
export const AI_MAX_RETRY_COUNT = 2;
export const AI_RETRY_WAIT_MS = [500, 1000]; // Linear backoff
export const ANALYZE_BATCH_SIZE = 1;
// ── Default Timezone ──
export const DEFAULT_TIMEZONE = 'Asia/Hong_Kong';
export const QUOTA_RESET_TIMEZONE = 'UTC';
// ── Currency (Phase 1: Single currency) ──
export const DEFAULT_CURRENCY = 'HKD';
// ── Usage Region (users.settings.usageRegion) ──
export * from './usageRegion.js';
// ── Weather greeting (HKO + AI sub-heading) ──
export * from './weatherGreeting.js';
//# sourceMappingURL=index.js.map